import axios from "axios";
import sha256 from 'crypto-js/sha256'
import {message} from "antd";

export const FILE_UPLOAD_URI = "/api/v1/file_upload"

// utils
const client = axios.create({
    baseURL: "/",
})
client.defaults.headers.common['TZ_TOKEN'] = localStorage["TZ_TOKEN"]

client.interceptors.response
    .use(
        response => {
            const {data} = response
            const {code, msg} = data
            if (data) {
                if (code * 1 !== 0) {
                    message.error(msg)
                }
            }
            return data
        },
        error => {
            message.error(error.message)
            return Promise.reject(error)
        })

export class ApiResponse {
    onSuccess = (handler) => {
        this.successHandler = handler;
        return this
    }
    onError = (handler) => {
        this.errorHandler = handler
        return this
    }
    onComplete = (handler) => {
        this.completeHandler = handler;
        return this
    }
}

export class ApiService {

    static user = {
        init(body) {
            return ApiService.postJson("/api/v1/user/init", body)
        },
        current() {
            return ApiService.get("/api/v1/user")
        },

        login(username, password) {
            let pwd = sha256(password).toString();
            pwd = sha256(pwd).toString()
            return ApiService.post("/api/v1/user/login", {
                username: username,
                password: pwd
            })
        },

        logout() {
            return ApiService.post("/api/v1/user/logout")
        }
    }

    static common = {
        region: {
            get(code) {
                return ApiService.get("/api/v1/region", {code: code})
            },
            root() {
                return ApiService.get("/api/v1/region/root")
            },
            children(code) {
                return ApiService.get("/api/v1/region/children", {currentCode: code})
            },
            parent(code) {
                return ApiService.get("/api/v1/region/parent", {currentCode: code})
            },
        }
    }

    static constants = {
        religion() {
            return ApiService.get("/api/v1/constants/religion")
        },
        educationBackgrounds() {
            return ApiService.get("/api/v1/constants/education_background")
        },
        nation() {
            return ApiService.get("/api/v1/constants/nation")
        },
        party() {
            return ApiService.get("/api/v1/constants/party")
        }
    }

    static admin = {
        system: {
            operation: {
                list() {
                    return ApiService.get("/api/v1/admin/system/operation/list")
                },
                get(name) {
                    return ApiService.get("/api/v1/admin/system/operation", {name: name})
                }
            },
            log: {
                list() {
                    return ApiService.get("/api/v1/admin/system/log/list")
                },
                get(name) {
                    return ApiService.get("/api/v1/admin/system/log", {name: name})
                }
            },

            user: {
                roles() {
                    return ApiService.get("/api/v1/admin/user/roles")
                },
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/user/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/user", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/user", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/user", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/user', {guid: guid})
                },
                access: {
                    list(userId) {
                        return ApiService.get("/api/v1/admin/user/access", {userId: userId})
                    },
                    update(userId, accesses) {
                        return ApiService.postJson("/api/v1/admin/user/access", {userId: userId, accesses: accesses})
                    }
                },
            },

            properties: {
                names() {
                    return ApiService.get("/api/v1/admin/property/system/names")
                },
                list(page = 0, size = 100) {
                    return ApiService.get("/api/v1/admin/property/system/list", {page: page, size: size})
                },
                get(name) {
                    return ApiService.get("/api/v1/admin/property/system", {name: name})
                },
                createOrUpdate(body) {
                    return ApiService.post("/api/v1/admin/property/system", body)
                },
                remove(name) {
                    return ApiService.delete('/api/v1/admin/property/system', {name: name})
                },

                shared: {
                    list(page = 0, size = 100) {
                        return ApiService.get("/api/v1/admin/property/shared/list", {page: page, size: size})
                    },
                    get(name) {
                        return ApiService.get("/api/v1/admin/property/shared", {name: name})
                    },
                    createOrUpdate(body) {
                        return ApiService.post("/api/v1/admin/property/shared", body)
                    },
                    remove(name) {
                        return ApiService.delete('/api/v1/admin/property/shared', {name: name})
                    },
                }
            }
        },
        acfic: {
            member: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/acfic/member/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/acfic/member", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/acfic/member", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/acfic/member", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/acfic/member', {guid: guid})
                }
            },
            count: {
                names() {
                    return ApiService.get("/api/v1/admin/acfic/count/names")
                },
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/acfic/count/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/acfic/count", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/acfic/count", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/acfic/count", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/acfic/count', {guid: guid})
                }
            }
        },
        hmto: {
            member: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/hmto/member/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/hmto/member", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/hmto/member", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/hmto/member", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/hmto/member', {guid: guid})
                }
            },
            hkEnterprise: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/hk_enterprise/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/hk_enterprise", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/hk_enterprise", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/hk_enterprise", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/hk_enterprise', {guid: guid})
                }
            },
            twEnterprise: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/tw_enterprise/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/tw_enterprise", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/tw_enterprise", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/tw_enterprise", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/tw_enterprise', {guid: guid})
                }
            },
        },
        regionStatistics: {
            list() {
                return ApiService.get('/api/v1/admin/home_map_data/list')
            },
            get(code) {
                return ApiService.get('/api/v1/admin/home_map_data', {code: code})
            },
            update(body) {
                return ApiService.postJson('/api/v1/admin/home_map_data', body)
            }
        },
        newStratum: {
            member: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/new_stratum_personnel/member/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/new_stratum_personnel/member", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/new_stratum_personnel/member", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/new_stratum_personnel/member", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/new_stratum_personnel/member', {guid: guid})
                }
            },
            workUnit: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/new_stratum_work_unit/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/new_stratum_work_unit", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/new_stratum_work_unit", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/new_stratum_work_unit", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/new_stratum_work_unit', {guid: guid})
                }
            }
        },
        partyWork: {
            nonalignedRepresentative: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/nonaligned_representative/member/list", {
                        page: page,
                        size: size
                    })
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/nonaligned_representative/member", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/nonaligned_representative/member", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/nonaligned_representative/member", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/nonaligned_representative/member', {guid: guid})
                }
            },
            nonpartyIntellectuals: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/nonparty_intellectuals/member/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/nonparty_intellectuals/member", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/nonparty_intellectuals/member", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/nonparty_intellectuals/member", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/nonparty_intellectuals/member', {guid: guid})
                }
            },
            partyMember: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/party/member/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/party/member", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/party/member", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/party/member", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/party/member', {guid: guid})
                }
            },
        },
        religion: {
            venue: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/religion/venue/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/religion/venue", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/religion/venue", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/religion/venue", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/religion/venue', {guid: guid})
                }
            },
            count: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/religion/count/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/religion/count", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/religion/count", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/religion/count", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/religion/count', {guid: guid})
                }
            }
        },
        nation: {
            count: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/nation/count/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/nation/count", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/nation/count", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/nation/count", body)
                },
                remove(guid) {
                    return ApiService.delete('/api/v1/admin/nation/count', {guid: guid})
                }
            }
        },
        camera: {
            list(page, venueId = 0, size = 20) {
                return ApiService.get("/api/v1/admin/camera/list", {page: page, venueId: venueId, size: size})
            },
            get(guid) {
                return ApiService.get("/api/v1/admin/camera", {guid: guid})
            },
            create(body) {
                return ApiService.postJson("/api/v1/admin/camera", body)
            },
            update(body) {
                return ApiService.putJson("/api/v1/admin/camera", body)
            },
            remove(guid) {
                return ApiService.delete("/api/v1/admin/camera", {guid: guid})
            },

            config: {
                list(page, size = 20) {
                    return ApiService.get("/api/v1/admin/camera/server/tiandy/list", {page: page, size: size})
                },
                get(guid) {
                    return ApiService.get("/api/v1/admin/camera/server/tiandy", {guid: guid})
                },
                create(body) {
                    return ApiService.postJson("/api/v1/admin/camera/server/tiandy", body)
                },
                update(body) {
                    return ApiService.putJson("/api/v1/admin/camera/server/tiandy", body)
                },
                remove(guid) {
                    return ApiService.delete("/api/v1/admin/camera/server/tiandy", {guid: guid})
                }
            }
        }
    }

    static get = (uri, params) => {
        const resp = new ApiResponse()
        let _uri = uri
        if (params) {
            _uri = _uri + "?" + Object.keys(params)
                .map(k => {
                    return k + "=" + encodeURIComponent(params[k])
                })
                .join("&")
        }
        client.get(_uri)
            .then(response => {
                const {code, data, msg} = response
                resp.completeHandler && resp.completeHandler()
                if (code * 1 === 0) {
                    resp.successHandler && resp.successHandler(data)
                } else {
                    resp.errorHandler && resp.errorHandler(msg, code)
                }
            })
            .catch(error => {
                resp.completeHandler && resp.completeHandler()
                resp.errorHandler && resp.errorHandler(error ?? '未知错误', -1)
            })
        return resp
    }
    static delete = (uri, params, config) => {
        const resp = new ApiResponse()
        let _uri = uri
        if (params) {
            _uri = _uri + "?" + Object.keys(params)
                .map(k => {
                    return k + "=" + encodeURIComponent(params[k])
                })
                .join("&")
        }
        client.delete(_uri, config)
            .then(response => {
                const {code, data, msg} = response
                if (code * 1 === 0) {
                    resp.successHandler && resp.successHandler(data)
                } else {
                    resp.errorHandler && resp.errorHandler(msg, code)
                }
                resp.completeHandler && resp.completeHandler()
            })
            .catch(error => {
                resp.errorHandler && resp.errorHandler(error ?? '未知错误', -1)
                resp.completeHandler && resp.completeHandler()
            })
        return resp
    }
    static putJson = (uri, object) => {
        const resp = new ApiResponse()
        client.put(uri, JSON.stringify(object), {
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(response => {
            const {code, data, msg} = response
            if (code * 1 === 0) {
                resp.successHandler && resp.successHandler(data)
            } else {
                resp.errorHandler && resp.errorHandler(msg, code)
            }
            resp.completeHandler && resp.completeHandler()
        }).catch(error => {
            resp.errorHandler && resp.errorHandler(error ?? '未知错误', -1)
            resp.completeHandler && resp.completeHandler()
        })
        return resp;
    }
    static postJson = (uri, object) => {
        const resp = new ApiResponse()
        client.post(uri, JSON.stringify(object), {
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(response => {
            const {code, data, msg} = response
            if (code * 1 === 0) {
                resp.successHandler && resp.successHandler(data)
            } else {
                resp.errorHandler && resp.errorHandler(msg, code)
            }
            resp.completeHandler && resp.completeHandler()
        }).catch(error => {
            resp.errorHandler && resp.errorHandler(error ?? '未知错误', -1)
            resp.completeHandler && resp.completeHandler()
        })
        return resp;
    }
    static postData = (uri, body, config) => {
        const resp = new ApiResponse()
        const formData = new FormData()
        for (const key of Object.keys(body)) {
            formData.append(key, body[key])
        }
        client.post(uri, formData, config)
            .then(response => {
                const {code, data, msg} = response
                if (code * 1 === 0) {
                    resp.successHandler && resp.successHandler(data)
                } else {
                    resp.errorHandler && resp.errorHandler(msg, code)
                }
                resp.completeHandler && resp.completeHandler()
            })
            .catch(error => {
                resp.errorHandler && resp.errorHandler(error ?? '未知错误', -1)
                resp.completeHandler && resp.completeHandler()
            })
        return resp
    }
    static post = (uri, params, config) => {
        const resp = new ApiResponse()
        const _params = new URLSearchParams();
        if (params) {
            for (const key of Object.keys(params)) {
                const value = params[key]
                _params.append(key, value);
            }
        }
        client.post(uri, _params, config)
            .then(response => {
                const {code, data, msg} = response
                if (code * 1 === 0) {
                    resp.successHandler && resp.successHandler(data)
                } else {
                    resp.errorHandler && resp.errorHandler(msg, code)
                }
                resp.completeHandler && resp.completeHandler()
            })
            .catch(error => {
                resp.errorHandler && resp.errorHandler(error ?? '未知错误', -1)
                resp.completeHandler && resp.completeHandler()
            })
        return resp
    }
}


